import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Clock, ArrowLeft, User } from "lucide-react";
import quizService from "../../services/quizService";
import { Quiz, QuizAttempt } from "../../types/quiz";

interface QuizGradingDashboardProps {}

const QuizGradingDashboard: React.FC<QuizGradingDashboardProps> = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [selectedAttempt, setSelectedAttempt] = useState<QuizAttempt | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [grading, setGrading] = useState(false);
  const [grades, setGrades] = useState<Record<string, number>>({});
  const [feedback, setFeedback] = useState<Record<string, string>>({});

  useEffect(() => {
    if (quizId) {
      loadQuizAndAttempts();
    }
  }, [quizId]);

  const loadQuizAndAttempts = async () => {
    try {
      setLoading(true);
      const [quizData, attemptsData] = await Promise.all([
        quizService.getQuiz(quizId!),
        quizService.getQuizAttempts(quizId!),
      ]);

      setQuiz(quizData);
      // Filter attempts that need manual grading
      const pendingAttempts = attemptsData.attempts.filter(
        (attempt: QuizAttempt) =>
          attempt.status === "submitted_pending_grading" ||
          attempt.status === "partially_graded"
      );
      setAttempts(pendingAttempts);
    } catch (error) {
      console.error("Error loading quiz and attempts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGradeAttempt = async (attempt: QuizAttempt, grades: any) => {
    try {
      setGrading(true);
      await quizService.gradeQuizAttempt(attempt._id!, grades, feedback);
      // Reload attempts after grading
      await loadQuizAndAttempts();
      setSelectedAttempt(null);
      setGrades({});
      setFeedback({});
    } catch (error) {
      console.error("Error grading attempt:", error);
    } finally {
      setGrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate("/elearning/quizzes")}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Grade Quiz: {quiz?.title}
            </h1>
            <p className="text-gray-600">
              {attempts.length} submissions pending grading
            </p>
          </div>
        </div>
      </div>

      {/* Attempts List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attempts List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Pending Submissions
          </h2>
          {attempts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No submissions pending grading
            </div>
          ) : (
            attempts.map((attempt) => (
              <div
                key={attempt._id}
                className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                  selectedAttempt?._id === attempt._id
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => {
                  setSelectedAttempt(attempt);
                  setGrades({});
                  setFeedback({});
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <User className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900">
                        {typeof attempt.studentId === "string"
                          ? attempt.studentId
                          : `${attempt.studentId?.firstName || ""} ${
                              attempt.studentId?.lastName || ""
                            }`}
                      </p>
                      <p className="text-sm text-gray-600">
                        Submitted{" "}
                        {new Date(
                          attempt.submittedAt || attempt.completedAt || ""
                        ).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {Math.floor((attempt.timeSpent || 0) / 60)}m{" "}
                      {(attempt.timeSpent || 0) % 60}s
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Grading Panel */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Grade Submission
          </h2>
          {selectedAttempt ? (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900">Student Answers</h3>
                  <div className="mt-4 space-y-4">
                    {selectedAttempt.answers
                      .filter((answer) => answer.needsManualGrading)
                      .map((answer, index) => {
                        // Find the question to get its points
                        const question = quiz?.questions?.find(
                          (q) => q._id === answer.questionId
                        );
                        const maxPoints = question?.points || 10;

                        return (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <h4 className="font-medium text-gray-900">
                                Question {index + 1}
                              </h4>
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={maxPoints}
                                  value={grades[answer.questionId] || ""}
                                  onChange={(e) =>
                                    setGrades((prev) => ({
                                      ...prev,
                                      [answer.questionId]:
                                        parseFloat(e.target.value) || 0,
                                    }))
                                  }
                                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                                  placeholder="Points"
                                />
                                <span className="text-sm text-gray-600">
                                  / {maxPoints} pts
                                </span>
                              </div>
                            </div>
                            <div className="bg-gray-50 p-3 rounded text-sm">
                              {answer.answer}
                            </div>
                            <div className="mt-3">
                              <textarea
                                placeholder="Add feedback..."
                                value={feedback[answer.questionId] || ""}
                                onChange={(e) =>
                                  setFeedback((prev) => ({
                                    ...prev,
                                    [answer.questionId]: e.target.value,
                                  }))
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                rows={2}
                              />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                    onClick={() => setSelectedAttempt(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    disabled={grading}
                    onClick={() => handleGradeAttempt(selectedAttempt, grades)}
                  >
                    {grading ? "Saving..." : "Save Grades"}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
              Select a submission to grade
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizGradingDashboard;
